import {constructRow, memo} from '@tanstack/react-table'

import {getGroupingMetadataFromServerGroupValue} from '../../../features/grouping/get-grouping-metadata'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {getRowId, groupedRowId} from '../helpers/get-row-id'
import type {CoreTable, CustomGroupingRow, Row, RowModel} from '../helpers/types'
import {getSubIssueSubRows} from './get-sub-issue-sub-rows'

/**
 * Reads pre-grouped item data from the query cache and formats it as a TanStack Table row model.
 *
 * @link [Customizing row models](https://tanstack.com/table/latest/docs/guide/row-models#customizefork-row-models)
 */
export function getCustomGroupedRowModel(): (table: CoreTable<MemexItemModel>) => () => RowModel<MemexItemModel> {
  return table => {
    return memo({
      memoDeps: () => [
        table.atoms.grouping.get(),
        table.getPreGroupedRowModel(),
        table.atoms.groupQueryData.get(),
        table.atoms.subIssuesQueryData.get(),
        table.getAllColumns(),
      ],
      fn: (grouping, rowModel, groupQueryData, subIssuesQueryData, columns) => {
        const {groupedItemQueries, queriesForGroups, groupsById} = groupQueryData
        const {subIssuesByParentId, copilotTasksEnabled} = subIssuesQueryData
        rowModel.flatRows = []
        rowModel.rowsById = {}

        const groupedByColumn = columns.find(column => column.id === grouping[0])?.columnDef.meta?.columnModel
        if (!groupedItemQueries || !groupedByColumn) {
          for (const row of rowModel.rows) {
            row.depth = 0
            row.parentId = undefined
            rowModel.rowsById[row.id] = row
            rowModel.flatRows.push(row)
            row.subRows = getSubIssueSubRows(row, subIssuesByParentId, table, rowModel, {
              copilotTasksEnabled,
            })
          }
          return rowModel
        }

        // Table view currently supports one layer of grouping.
        const groupByDepth = 0
        // Track the relative position of an item row within the full table.
        let itemsIndex = 0

        const groupedRows = queriesForGroups
          .flatMap(query => query?.data?.groups || [])
          .map((group, index) => {
            const {groupId} = group
            const itemQueries = groupedItemQueries[groupId]
            const groupQuery = groupsById[groupId]

            if (!groupQuery || !itemQueries) {
              // groupedItemQueries and groupsById should be updated in lock step, so both
              // lookups should succeed. If they do not, omit this group from the result.
              return undefined
            }

            const getGroupSubRows = (parentId: string, depth: number) => {
              const items = itemQueries.reduce((all, current) => {
                all.push(...(current?.data?.nodes || []))
                return all
              }, [] as Array<MemexItemModel>)

              return items.map(item => {
                // An item row's index is relative to all item rows in the table, whether or not
                // grouping is applied. Memex uses it for the row number in the leading drag handle.
                const rowId = getRowId(item, itemsIndex, {id: parentId})
                const itemRow = constructRow(table, rowId, item, itemsIndex, depth, undefined, parentId)
                rowModel.flatRows.push(itemRow)
                itemRow.subRows = getSubIssueSubRows(itemRow, subIssuesByParentId, table, rowModel, {
                  copilotTasksEnabled,
                })
                rowModel.rowsById[itemRow.id] = itemRow
                itemsIndex += 1
                return itemRow
              })
            }

            const groupRowId = groupedRowId(groupedByColumn.id, groupId)
            // Record the position before adding this group's item rows.
            const startIndex = rowModel.flatRows.length - 1
            const subRows = getGroupSubRows(groupRowId, groupByDepth + 1)
            const {groupValue, groupMetadata, totalCount} = groupQuery
            const groupedSourceObject = getGroupingMetadataFromServerGroupValue(
              groupedByColumn,
              groupValue,
              groupMetadata,
            )
            if (!groupedSourceObject) return

            /**
             * Use the first item to build the grouped row for consistency with native behavior:
             * "If the row is a grouped row, the original row object will be the first original in the group."
             * @link [API docs](https://tanstack.com/table/latest/docs/api/core/row#original)
             */
            const firstItem = subRows[0]?.original
            if (!firstItem) return
            // Omitting CustomGroupingRow properties here produces a type error if one is
            // omitted when groupedRow is built below.
            const baseRow: Omit<Row<MemexItemModel>, keyof CustomGroupingRow> = constructRow(
              table,
              groupRowId,
              firstItem,
              index,
              groupByDepth,
            )
            const groupedRow: Row<MemexItemModel> = Object.assign(baseRow, {
              groupingColumnId: groupedByColumn.id.toLocaleString(),
              groupingValue: groupId,
              subRows,
              // Custom property.
              groupQueryData: groupQuery,
              // Deprecated properties retained for backwards compatibility.
              groupedValue: groupId,
              groupedSourceObject,
              totalCount: totalCount.value,
            })
            // Insert the grouped row into flatRows before its children.
            rowModel.flatRows.splice(startIndex + 1, 0, groupedRow)
            rowModel.rowsById[groupedRow.id] = groupedRow
            return groupedRow
          })
          .filter(row => !!row)

        return {
          /** Parent group rows only. */
          rows: groupedRows,
          /**
           * Parent group rows and leaf item rows, ordered for rendering.
           * Use row.getIsGrouped() to distinguish parent group rows.
           */
          flatRows: rowModel.flatRows,
          /**
           * Parent group rows and leaf item rows, keyed by row ID.
           * Use row.getIsGrouped() to distinguish parent group rows.
           */
          rowsById: rowModel.rowsById,
        }
      },
    })
  }
}
